import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { trpc } from '@/config/trpc'
import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/shadcn/form'

type Props = {
  email: string
  onEmailChange: (email: string) => void
  onSwitchToLogin: () => void
}

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Please enter a valid email address' }),
})

export function ForgotPassword({ email, onEmailChange, onSwitchToLogin }: Props) {
  const [isSent, setIsSent] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
    },
  })

  const { mutateAsync: sendPasswordResetLink, isPending } =
    trpc.auth.sendPasswordResetLink.useMutation({
      onError: (error) =>
        form.setError('root', {
          message: error.message,
        }),
    })

  const watchedEmail = form.watch('email') // eslint-disable-line react-hooks/incompatible-library -- React Hook Form API

  useEffect(() => {
    if (watchedEmail !== email) {
      onEmailChange(watchedEmail)
    }
  }, [watchedEmail, email, onEmailChange])

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    await sendPasswordResetLink({ email: values.email })
    setIsSent(true)
  }

  return (
    <div>
      {isSent ? (
        <p className="text-center text-sm sm:text-base">
          Password reset email sent! Please check your inbox.
        </p>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Email" size="lg" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root && (
              <div className="mt-1">
                <FormMessage>{form.formState.errors.root.message}</FormMessage>
              </div>
            )}

            <Button
              type="submit"
              disabled={isPending}
              className="mt-4 w-full"
              size="lg"
              variant="primary">
              {isPending ? (
                <span className="border-primary/30 border-t-primary inline-block h-4 w-4 animate-spin rounded-full border-2" />
              ) : (
                'Send Password Reset Link'
              )}
            </Button>
          </form>
        </Form>
      )}
      <p className="text-muted-foreground mt-2 text-center text-xs">
        Back to{' '}
        <a
          className="text-primary cursor-pointer underline"
          onClick={(e) => {
            e.preventDefault()
            onSwitchToLogin()
          }}>
          login
        </a>
        .
      </p>
    </div>
  )
}
